import { type ReactNode } from 'react';

import { type Theme, THEME_OPTIONS, useThemeStore } from '@/stores/theme';

import styles from './ThemeToggle.module.css';

const ICONS: Record<Theme, ReactNode> = {
    light: (
        <>
            <circle cx="12" cy="12" r="4.2" />
            <path d="M12 2.6v2.2M12 19.2v2.2M2.6 12h2.2M19.2 12h2.2M5.4 5.4l1.6 1.6M17 17l1.6 1.6M18.6 5.4 17 7M7 17l-1.6 1.6" />
        </>
    ),
    dark: <path d="M20 13.4A8.4 8.4 0 0 1 10.6 4a8.4 8.4 0 1 0 9.4 9.4Z" />,
};

/**
 * Light / dark switcher.
 *
 * A two-option radiogroup with a sliding indicator. There is no "system"
 * option: the operating-system preference still picks the theme for a
 * first-time visitor, but once someone has chosen, that choice is what they
 * get — see the note in `stores/theme.ts`.
 *
 * Icon-only, with the words kept for screen readers. A sun and a moon are about
 * as unambiguous as an icon pair gets, and the control sits in a header where
 * every millimetre is contested.
 */
export function ThemeToggle() {
    const theme = useThemeStore((state) => state.theme);
    const setTheme = useThemeStore((state) => state.setTheme);

    return (
        <div className={styles.toggle} role="radiogroup" aria-label="Weergave">
            <span
                className={`${styles.thumb} ${theme === 'dark' ? styles.thumbDark : ''}`}
                aria-hidden="true"
            />

            {THEME_OPTIONS.map((option) => (
                <button
                    key={option.value}
                    type="button"
                    role="radio"
                    aria-checked={theme === option.value}
                    className={styles.option}
                    onClick={() => {
                        setTheme(option.value);
                    }}
                >
                    <svg
                        className={styles.icon}
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.7"
                        strokeLinecap="round"
                        aria-hidden="true"
                    >
                        {ICONS[option.value]}
                    </svg>
                    {/*
                        Visually hidden, not removed. The icon alone is what the
                        owner asked for — *"kun je in het dashboard deze switcher
                        zonder tekst doen? Dat maakt het allemaal wat compacter"*
                        — but the word is what gives the control its accessible
                        name, so it stays in the DOM. Dropping it would leave a
                        screen reader announcing "radio, unlabelled".
                    */}
                    <span className="visually-hidden">{option.label}</span>
                </button>
            ))}
        </div>
    );
}
